import { useEffect, useState } from 'react';
import { fetchArticleContent, CONTENT_TIERS } from '../../services/articleService.js';
import { formatRelative } from '../../utils/time.js';
import Icon from '../common/Icon.jsx';
import Skeleton from '../common/Skeleton.jsx';

/**
 * ArticleReader — full-screen in-app reading view for one news article.
 *
 * Replaces the old tap behaviour, which jumped straight to the Oracle with a
 * "give me more context on this headline" prompt — there was no way to read
 * the story the card was advertising. The Oracle hand-off survives as a button
 * at the foot of the article, where it has something to be *about*.
 *
 * Rendering contract
 * ------------------
 * The body arrives from articleService as typed blocks, never as markup, so
 * there is no dangerouslySetInnerHTML anywhere in this component. Third-party
 * HTML from six publishers is turned into data before it reaches React; the
 * switch below is the only thing that decides what an element becomes. That
 * also keeps the component portable to React Native — swap the tags, keep the
 * logic.
 *
 * Back handling
 * -------------
 * Owned by NewsScreen, not this component — see the note there. Escape stays
 * here because it is purely a reader affordance.
 */
export default function ArticleReader({ article, onClose, onAskOracle }) {
  const [state, setState] = useState({ status: 'loading', content: null });

  useEffect(() => {
    const ctrl = new AbortController();
    let active = true;

    fetchArticleContent(article, { signal: ctrl.signal })
      .then((content) => {
        if (active) setState({ status: 'ready', content });
      })
      .catch((err) => {
        if (active && err.name !== 'AbortError') {
          setState({ status: 'ready', content: null });
        }
      });

    return () => {
      active = false;
      ctrl.abort();
    };
  }, [article]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const content = state.content;
  const blocks = withoutDuplicateLead(content?.blocks ?? [], article.coverImage);
  const partial = content ? content.tier === CONTENT_TIERS.SUMMARY : false;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={article.title}
      className="absolute inset-0 z-30 flex flex-col bg-bg-base animate-slide-up"
    >
      <header className="flex shrink-0 items-center gap-2 border-b border-stroke bg-bg-base/95 px-3 py-2 backdrop-blur">
        <button
          onClick={onClose}
          aria-label="Back to feed"
          className="pressable flex h-9 w-9 items-center justify-center rounded-full bg-bg-card2 text-ink-secondary ring-1 ring-stroke hover:text-ink-primary"
        >
          <Icon name="chevronLeft" size={18} />
        </button>
        <span
          className="min-w-0 flex-1 truncate text-2xs font-bold uppercase tracking-widest-plus"
          style={{ color: article.accentColor }}
        >
          {article.source}
        </span>
        <a
          href={article.originalUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Open the original article on ${article.source}`}
          className="pressable flex h-9 w-9 items-center justify-center rounded-full bg-bg-card2 text-ink-secondary ring-1 ring-stroke hover:text-ink-primary"
        >
          <Icon name="external" size={15} />
        </a>
      </header>

      <div className="scroll-rail flex-1 overflow-y-auto overscroll-contain">
        {article.coverImage ? (
          <div className="relative h-44 w-full overflow-hidden">
            <img src={article.coverImage} alt="" className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-bg-base via-bg-base/40 to-transparent" />
          </div>
        ) : null}

        <article className={`px-5 pb-10 ${article.coverImage ? '-mt-6' : 'pt-5'}`}>
          <div className="relative mb-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-2xs font-semibold uppercase tracking-widest-plus text-ink-tertiary">
            <span>{article.dateEstimated ? 'Recent' : formatRelative(article.publishedAt)}</span>
            <span className="text-ink-muted">•</span>
            <span>{(content?.readTime ?? article.readTime) || 1} min read</span>
            {article.isBreaking ? (
              <>
                <span className="text-ink-muted">•</span>
                <span className="text-f1-red">Breaking</span>
              </>
            ) : null}
          </div>

          <h1 className="relative text-2xl font-bold leading-tight text-ink-primary">
            {article.title}
          </h1>

          <div
            aria-hidden="true"
            className="mt-4 h-px w-full"
            style={{ background: `linear-gradient(90deg, ${article.accentColor}, transparent)` }}
          />

          {state.status === 'loading' ? <ArticleSkeleton /> : null}

          {state.status === 'ready' ? (
            <>
              {partial ? <PartialNotice article={article} reason={content?.error} /> : null}

              <div className="mt-5 space-y-4">
                {blocks.map((block, i) => (
                  <Block key={`${block.type}-${i}`} block={block} />
                ))}
              </div>

              {blocks.length === 0 ? (
                <p className="mt-5 text-sm leading-relaxed text-ink-secondary">
                  No preview text came through for this story — open it on{' '}
                  {article.source} to read it in full.
                </p>
              ) : null}

              <div className="mt-8 space-y-2 border-t border-stroke pt-5">
                <a
                  href={article.originalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="pressable flex w-full items-center justify-center gap-2 rounded-xl bg-bg-card px-4 py-3 text-sm font-semibold text-ink-primary ring-1 ring-stroke hover:bg-bg-card2"
                >
                  <Icon name="external" size={14} />
                  Read on {article.source}
                </a>
                <button
                  onClick={() => onAskOracle(article)}
                  className="pressable flex w-full items-center justify-center gap-2 rounded-xl bg-f1-red px-4 py-3 text-sm font-semibold text-white shadow-glow-red-soft transition-colors hover:bg-f1-redBright"
                >
                  <Icon name="oracle" size={16} />
                  Ask the Oracle about this
                </button>
              </div>

              {content ? <SourceNote tier={content.tier} source={article.source} /> : null}
            </>
          ) : null}
        </article>
      </div>
    </div>
  );
}

/**
 * withoutDuplicateLead — the article's lead photo is shown as the header
 * image, and the page body opens with that same photo, so it rendered twice
 * in a row.
 *
 * The two URLs frequently differ: publishers serve the feed a different CDN
 * transform of the same asset (`.../w_600/foo.jpg` vs `.../w_3392/foo.jpg`),
 * so an equality check catches only some of them. Compare the filename, and
 * additionally drop a *leading* image whenever a cover is displayed — at the
 * top of an article that is the lead art by definition.
 */
function withoutDuplicateLead(blocks, coverImage) {
  if (!coverImage || blocks.length === 0) return blocks;

  const filename = (url) => {
    try {
      return new URL(url).pathname.split('/').filter(Boolean).pop() ?? '';
    } catch {
      return '';
    }
  };

  const coverName = filename(coverImage);
  const start = blocks[0].type === 'image' ? 1 : 0;

  return blocks
    .slice(start)
    .filter((b) => !(b.type === 'image' && (b.src === coverImage || (coverName && filename(b.src) === coverName))));
}

/**
 * Block — one whitelisted content node. Anything the extractor could not
 * classify never reaches here, so the default case is genuinely unreachable
 * rather than a silent escape hatch.
 */
function Block({ block }) {
  switch (block.type) {
    case 'heading':
      return (
        <h2
          className={`pt-2 font-bold leading-snug text-ink-primary ${
            block.level === 3 ? 'text-base' : 'text-lg'
          }`}
        >
          {block.text}
        </h2>
      );

    case 'quote':
      return (
        <blockquote className="border-l-2 border-f1-red/70 pl-4 text-sm italic leading-relaxed text-ink-secondary">
          {block.text}
        </blockquote>
      );

    case 'listItem':
      return (
        <div className="flex gap-2.5 text-sm leading-relaxed text-ink-secondary">
          <span aria-hidden="true" className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-f1-red" />
          <span>{block.text}</span>
        </div>
      );

    case 'image':
      return (
        <img
          src={block.src}
          alt={block.alt ?? ''}
          loading="lazy"
          className="w-full rounded-xl bg-bg-card2"
        />
      );

    case 'caption':
      return <p className="text-2xs leading-relaxed text-ink-tertiary">{block.text}</p>;

    case 'code':
      return (
        <pre className="overflow-x-auto rounded-xl bg-bg-card2 p-3 font-mono text-2xs text-ink-secondary ring-1 ring-stroke">
          {block.text}
        </pre>
      );

    default:
      return <p className="text-[15px] leading-relaxed text-ink-secondary">{block.text}</p>;
  }
}

/**
 * PartialNotice — shown when only the feed teaser is available. Says so
 * plainly: presenting a 40-word summary as though it were the article is the
 * one outcome worse than not having the reader at all.
 */
function PartialNotice({ article, reason }) {
  const detail =
    reason === 'no-readable-content'
      ? `${article.source} didn't return readable text for this one — it may be subscriber-only.`
      : `The full text couldn't be fetched right now.`;

  return (
    <div className="mt-5 rounded-xl bg-signal-amber/10 px-3 py-2.5 text-2xs leading-relaxed text-signal-amber ring-1 ring-signal-amber/30">
      Summary only. {detail} Open it on {article.source} for the whole story.
    </div>
  );
}

function SourceNote({ tier, source }) {
  // No leading article before the source name — several are already "The …"
  // ("the The Race F1 feed").
  const label =
    tier === CONTENT_TIERS.FEED
      ? `Full text supplied by ${source}`
      : tier === CONTENT_TIERS.PROXY
        ? `Full text extracted from ${source}`
        : `Feed summary from ${source}`;

  return (
    <p className="mt-5 text-center text-[10px] uppercase tracking-widest-plus text-ink-muted">
      {label}
    </p>
  );
}

function ArticleSkeleton() {
  return (
    <div className="mt-6 space-y-3" aria-hidden="true">
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-4/5" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-3/5" />
      <Skeleton className="h-32 w-full" rounded="rounded-xl" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-11/12" />
    </div>
  );
}
